import React from "react";
import { StyleSheet } from "react-native";
import { colors, layout } from "@/constants";
import Interactive from "./base/Interactive";
import Text from "./base/Text";
import Icon, { IconName } from "./base/Icon";

/**
 * Button variant types for different styling
 */
export type DrawerButtonVariant = "primary" | "danger";

/**
 * Props interface for the DrawerButton component
 */
interface DrawerButtonProps {
  /** The text to display inside the button */
  text: string;
  /** Icon name to display */
  icon?: IconName;
  /** Button styling variant */
  variant?: DrawerButtonVariant;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Whether this button represents the currently selected page */
  isSelected?: boolean;
  /** Callback function triggered when the button is pressed */
  onPress: () => void;
  /** Accessibility label for screen readers */
  accessibilityLabel?: string;
  /** Custom style override */
  style?: object;
}

/**
 * A specialized button component for drawer actions.
 * Provides consistent styling for different action types.
 *
 * @param props - The component props
 * @returns A drawer list item with icon and text
 */
export default function DrawerButton({
  text,
  icon,
  variant = "primary",
  disabled = false,
  isSelected = false,
  onPress,
  accessibilityLabel,
  style
}: DrawerButtonProps) {
  const color = disabled
    ? colors.onSurfaceMuted
    : variant === "danger"
      ? colors.error
      : colors.onSurface;

  return (
    <Interactive
      style={[styles.button, style]}
      onPress={onPress}
      disabled={disabled}
      selected={isSelected}
      accessibilityLabel={accessibilityLabel || text}
    >
      {icon && <Icon name={icon} size="large" style={{ color }} />}
      <Text style={[styles.text, { color }]}>{text}</Text>
    </Interactive>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: layout.space.sm,
    paddingHorizontal: layout.space.md,
    borderRadius: layout.borderRadius.medium
  },
  text: {
    marginLeft: layout.space.sm
  }
});
